import React, { useContext, useState, useEffect } from 'react'
import { Box, Label, Input, FormField, Tooltip } from 'pcln-design-system'
import { Search as SearchIcon, Warning as WarningIcon } from 'pcln-icons'
import Downshift, {
  ControllerStateAndHelpers,
  DownshiftState,
  StateChangeOptions
} from 'downshift'
import { useMachine } from '@xstate/react'
import fetch from 'isomorphic-unfetch'
import CONFIG from 'isomorphic-config'
import BootstrapDataContext from '@/context/BootstrapDataContext'
import { PopoverContainer } from '@/components/Popover'
import { getErrorFromKey } from '@/shared-utils/error-helpers'
import useRecentSearches from '@/hooks/useRecentSearches'
import {
  AIR_SEARCH_DATA,
  CAR_SEARCH_DATA,
  HOTEL_SEARCH_DATA,
  PACKAGE_SEARCH_DATA
} from '@/shared-utils/local-forage-helper'
import useRecommendations from '@/hooks/useRecommendations'
import { LOCATION_SEARCH_TYPE, TypeAheadConfigEndpoint } from '@/types'
import useTypeAheadForDisplayName from './useTypeAheadForDisplayName'
import geoMachine from '../../machines/currentLocationMachine'
import Dropdown from './Dropdown'
import typeAheadInputStyle from './StyledInput'

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {}

const errorProps = {
  id: `typeahead-error`,
  style: { zIndex: 1 },
  bottom: true,
  right: true,
  color: 'error',
  role: 'alert'
}

const mapTypeToCode = {
  AIRPORT: 'A',
  CITY: 'C',
  HOTEL: 'H',
  POI: 'P',
  GEO: 'GEO',
  LATLON: 'LATLON',
  PARTNER_LOC: 'PARTNER_LOC',
  GDS_CITY: 'GDS_CITY'
}

const recentSearchesKey = {
  cars: CAR_SEARCH_DATA,
  hotels: HOTEL_SEARCH_DATA,
  flights: AIR_SEARCH_DATA,
  packagesOrigin: PACKAGE_SEARCH_DATA,
  packagesDestination: PACKAGE_SEARCH_DATA
} as const

type TypeAheadProps = {
  searchProduct: TypeAheadConfigEndpoint
  label: string
  placeholder: string
  searchKey: string
  defaultSelectedItem: LOCATION_SEARCH_TYPE | null | Record<string, never>
  errors: unknown
  disableShadowState: () => void
  // eslint-disable-next-line @typescript-eslint/ban-types
  setValue?: Function
  onItemSelect?: (item: LOCATION_SEARCH_TYPE) => void
  focusNextElement?: () => unknown
  showCurrentLocation?: boolean
}

const TypeAhead = React.forwardRef<HTMLInputElement, TypeAheadProps>(
  (
    {
      label,
      placeholder,
      searchProduct,
      searchKey,
      setValue = noop,
      defaultSelectedItem,
      errors,
      disableShadowState,
      focusNextElement = noop,
      onItemSelect = noop,
      showCurrentLocation = false
    },
    ref
  ) => {
    const {
      isMobile,
      key,
      recommendedCityIds,
      signInStatus: { signedIn }
    } = useContext(BootstrapDataContext)

    const handleInputKeyDown = ({
      event,
      isOpen,
      selectHighlightedItem
    }: {
      isOpen: boolean
      event: React.KeyboardEvent<HTMLInputElement>
      selectHighlightedItem: () => void
    }) => {
      if (isOpen && ['Tab'].includes(event.key)) {
        event.preventDefault()
        selectHighlightedItem()
        focusNextElement()
      }
    }

    const typeAheadEndpoint = searchProduct
    const currentRecentSearchesKey = recentSearchesKey[typeAheadEndpoint]
    const [recentStartLocations, recentEndLocations] = useRecentSearches(
      currentRecentSearchesKey
    )

    const recentSearches = searchKey.includes('startLocation')
      ? recentStartLocations
      : recentEndLocations

    const handleTypeAheadFeedback = ({
      entered,
      id,
      itemName,
      displayName,
      type,
      selectedPosition
    }: LOCATION_SEARCH_TYPE & { selectedPosition: number | null }) => {
      // swagger for feedback call api http://ny-htlindex-001:8080/typeahead/index/swagger-ui.html#/feedback-controller/typeaheadFeedbackWithSelectedPositionUsingGET
      const url = `${
        CONFIG.client.typeahead.feedback[typeAheadEndpoint]
      }/${id}/${encodeURIComponent(entered)}/${encodeURIComponent(
        itemName || displayName || ''
      )}/${selectedPosition ?? ''}`
      const queryParams = `language=en&locale=en-US&placeType=${mapTypeToCode[type]}`
      fetch(`${url}?${queryParams}`)
    }

    const [isPopoverOpen, setIsPopoverOpen] = useState(false)

    type HandleSelectType = (
      fn: () => void
    ) => (
      selectedItem: any,
      stateAndHelpers: ControllerStateAndHelpers<any>
    ) => void

    const handleSelect: HandleSelectType =
      disableShadowEffect =>
      (selectedItem, { reset, highlightedIndex }) => {
        setValue(searchKey, selectedItem)
        onItemSelect(selectedItem)
        reset()
        if (selectedItem) {
          handleTypeAheadFeedback({
            ...selectedItem,
            selectedPosition: highlightedIndex
          })
          disableShadowEffect()
          setIsPopoverOpen(false)
        }
      }

    const stateReducer = (
      state: DownshiftState<LOCATION_SEARCH_TYPE>,
      changes: StateChangeOptions<LOCATION_SEARCH_TYPE>
    ) => {
      switch (changes.type) {
        case Downshift.stateChangeTypes.keyDownEnter:
        case Downshift.stateChangeTypes.clickItem:
        case Downshift.stateChangeTypes.mouseUp: // case for item selection on input blur
        case undefined: // handle case for item selection when you use TAB key. This seems to be a bug in Downshift. As per the type system, changes.type cannot be null
        case Downshift.stateChangeTypes.unknown: {
          const { highlightedIndex, inputValue } = state
          if (
            state.isOpen &&
            Object.prototype.hasOwnProperty.call(changes, 'isOpen')
          ) {
            return {
              ...changes,
              // persist value of highlightedIndex after item selection
              // Normally highlightedIndex changes to defaultHighlightedIndex after selection
              highlightedIndex,
              inputValue
            }
          }
          return changes
        }
        default:
          return changes
      }
    }

    const itemDisplayNameTransform = useTypeAheadForDisplayName()
    const itemToString = (
      i: LOCATION_SEARCH_TYPE | Record<string, never> | null
    ) => {
      return itemDisplayNameTransform?.(i) ?? i?.itemName ?? ''
    }

    const errorMessage = getErrorFromKey(searchKey)(errors)

    const [current, send, service] = useMachine(geoMachine)

    const currentLocationProps = {
      current,
      showCurrentLocation,
      handleRequestCurrentLocationClick: () => {
        send('REQUEST')
      }
    }

    useEffect(() => {
      const subscription = service.subscribe(state => {
        if (state.matches('resolved')) {
          disableShadowState()
          setValue(searchKey, state.context.cityData)
          onItemSelect(state.context.cityData)
          setIsPopoverOpen(false)
        }
      })

      return () => subscription.unsubscribe()
    }, [service, setValue, searchKey, onItemSelect, disableShadowState])

    const isHotelTypeaheadOnHP =
      typeAheadEndpoint === 'hotels' && key === 'home'

    const canDisplayRecommendations =
      isHotelTypeaheadOnHP &&
      signedIn &&
      recommendedCityIds &&
      recommendedCityIds?.cityId?.length > 0

    const [recentOrRecommendationData, setRecentOrRecommendationData] =
      useState<LOCATION_SEARCH_TYPE[]>([])
    const [recentOrRecommendationTitle, setRecentOrRecommendationTitle] =
      useState('')
    const recommendedSearches = useRecommendations(
      canDisplayRecommendations,
      recommendedCityIds
    )
    useEffect(() => {
      if (canDisplayRecommendations) {
        setRecentOrRecommendationData(
          recommendedSearches as unknown as LOCATION_SEARCH_TYPE[]
        )
        setRecentOrRecommendationTitle('Recommended Searches')
      } else {
        setRecentOrRecommendationData(recentSearches)
        setRecentOrRecommendationTitle('Recent Searches')
      }
    }, [
      recommendedCityIds,
      recommendedSearches,
      recentSearches,
      canDisplayRecommendations
    ])

    return (
      <Downshift
        stateReducer={stateReducer}
        selectedItem={defaultSelectedItem}
        defaultHighlightedIndex={0}
        onSelect={handleSelect(disableShadowState)}
        itemToString={itemToString}
        id={`${searchKey}-typeahead-downshift-container`}
      >
        {({
          getLabelProps,
          getInputProps,
          getRootProps,
          openMenu,
          clearSelection,
          selectHighlightedItem,
          isOpen,
          inputValue,
          highlightedIndex,
          ...restProps
        }) => (
          <Box
            width={1}
            style={{ position: 'relative' }}
            {...getRootProps({
              refKey: 'ref'
            })}
          >
            <PopoverContainer
              open={isPopoverOpen}
              onDismiss={() => setIsPopoverOpen(false)}
            >
              <Box
                role="combobox"
                aria-expanded={isOpen ? 'true' : 'false'}
                aria-owns="typeahead-list"
                aria-haspopup="listbox"
              >
                <FormField>
                  <Label
                    pb={0}
                    autoHide={label === placeholder}
                    width="auto"
                    {...getLabelProps()}
                  >
                    {label}
                  </Label>
                  <SearchIcon color="primary" size={24} />
                  <Input
                    color={errorMessage ? 'red' : undefined}
                    ref={ref}
                    borderRadius="lg"
                    id={`${searchKey}-typeahead-input`}
                    data-testid={`${searchKey}-typeahead-input`}
                    autoComplete="off"
                    {...getInputProps({
                      placeholder,
                      name: searchKey,
                      style: typeAheadInputStyle(inputValue, label),
                      isOpen,
                      onKeyDown: (
                        event: React.KeyboardEvent<HTMLInputElement>
                      ) => {
                        handleInputKeyDown({
                          event,
                          selectHighlightedItem,
                          isOpen
                        })
                        setIsPopoverOpen(true)
                      },
                      onKeyUp: (
                        event: React.KeyboardEvent<HTMLInputElement>
                      ) => {
                        if (event.key === 'Escape') {
                          setIsPopoverOpen(false)
                        }
                      },
                      onChange: (
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        if (event.target.value === '') {
                          clearSelection()
                        }
                      },
                      onClick: (event: React.MouseEvent<HTMLInputElement>) => {
                        event.preventDefault()
                        const target = event.target as HTMLInputElement
                        if (target.value !== '') {
                          target.select()
                          openMenu()
                        }
                        setIsPopoverOpen(true)
                      },
                      onBlur: () => {
                        setIsPopoverOpen(false)
                      },
                      onFocus: (event: React.FocusEvent<HTMLInputElement>) => {
                        if (
                          showCurrentLocation ||
                          recentOrRecommendationData.length ||
                          event.target.value !== ''
                        ) {
                          openMenu()
                        }
                        setIsPopoverOpen(true)
                      }
                    })}
                  />
                  {errorMessage && <WarningIcon color="error" size={20} />}
                </FormField>
                {errorMessage && (
                  <Tooltip {...errorProps}>{errorMessage}</Tooltip>
                )}
                {/* For accessibility reasons for desktop, the results are inside the container with role='combobox' */}
                {!isMobile && (
                  <Dropdown
                    isOpen={isOpen}
                    searchKey={searchKey}
                    searchValue={inputValue}
                    isMobile={isMobile}
                    typeAheadEndpoint={typeAheadEndpoint}
                    highlightedIndex={highlightedIndex}
                    recentSearches={recentOrRecommendationData}
                    recentOrRecommendationTitle={recentOrRecommendationTitle}
                    label={label}
                    {...restProps}
                  />
                )}
              </Box>
              {isMobile && (
                <Dropdown
                  isOpen={isOpen}
                  searchKey={searchKey}
                  searchValue={inputValue}
                  isMobile={isMobile}
                  typeAheadEndpoint={typeAheadEndpoint}
                  highlightedIndex={highlightedIndex}
                  recentSearches={recentOrRecommendationData}
                  recentOrRecommendationTitle={recentOrRecommendationTitle}
                  label={label}
                  {...currentLocationProps}
                  {...restProps}
                />
              )}
            </PopoverContainer>
          </Box>
        )}
      </Downshift>
    )
  }
)

export default TypeAhead
